/**
 * PATH: packages/ui/src/kanban/api-fetch/access-locks.ts
 * FILE_ID: SAFETY-FETCH-LOCKQUEUE-012
 * PURPOSE: fetch implementation of KanbanAccessLockApi (verifier queue).
 * WHY: The access-lock lifecycle API lives on the organisation app at
 *   `/api/access-locks/` — a sibling of `/api/kanban/` — so it can't go
 *   through the kanban-rooted `_request`. Mirrors the derived-base inline
 *   fetch pattern `requestAccessUnlock` (api-fetch/cards.ts) established.
 * FLOW:
 *   ctx.baseUrl (…/api/kanban) → derived …/api/access-locks base
 *     → GET pending/ | POST {id}/grant/ | POST {id}/deny/
 * RELATED:
 *   - ../api/contract-access-locks.ts (the contract this implements)
 *   - organisation/urls/access_locks.py (backend routes)
 */
import { formatDrfError } from '../formatDrfError'
import type { KanbanAccessLockApi, PendingUnlockRequest } from '../api/contract-access-locks'
import type { ApiFetchContext } from './context'

export function createAccessLocksApi(ctx: ApiFetchContext): KanbanAccessLockApi {
  const { baseUrl, getAuthHeaders } = ctx
  const accessLockBase = baseUrl.replace(/\/kanban\/?$/, '/access-locks').replace(/\/+$/, '')

  async function lockRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }
    if (getAuthHeaders) {
      Object.assign(headers, await getAuthHeaders())
    }
    const res = await fetch(`${accessLockBase}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'include',
    })
    if (!res.ok) {
      let err = `Access-lock request failed (${res.status})`
      try {
        const parsed = formatDrfError(await res.json())
        if (parsed) err = parsed
      } catch {
        // keep the status-coded message
      }
      const apiError: Error & { status?: number } = new Error(err)
      apiError.status = res.status
      throw apiError
    }
    return (await res.json()) as T
  }

  return {
    listPendingUnlockRequests: () =>
      lockRequest<{ requests: PendingUnlockRequest[] }>('GET', '/pending/'),

    grantAccessUnlock: (lockId, opts) =>
      lockRequest<{ grant_id: number; lock_state: string; expires_at: string | null }>(
        'POST',
        `/${lockId}/grant/`,
        {
          request_id: opts.requestId,
          scope: opts.scope,
          duration_hours: opts.durationHours,
        },
      ),

    denyAccessUnlock: (lockId, opts) =>
      lockRequest<{ lock_state: string }>('POST', `/${lockId}/deny/`, {
        request_id: opts.requestId,
        reason: opts.reason,
      }),
  }
}
